import { ConfirmDialog as UiConfirmDialog } from '@Pages/pageRuntime/components/ui/dialog/ConfirmDialog';
import type React from 'react';

type ConfirmDialogProps = {
  /**
   * Dialog heading.
   */
  title: React.ReactNode;

  /**
   * Explanation of the action.
   */
  description: React.ReactNode;

  /**
   * Confirm button label.
   */
  confirmLabel?: string;

  /**
   * Called when the confirm action is selected. Return a promise to show a processing
   * indicator until it settles. The dialog closes when the promise resolves. It stays open
   * when the handler returns `false`, resolves `false`, or rejects, so the page can show the
   * error in the dialog.
   */
  onConfirm: () => void | boolean | Promise<void | boolean>;

  /**
   * Element that opens the dialog.
   */
  trigger?: React.ReactNode;

  /**
   * Show a processing indicator on the confirm action.
   */
  processing?: boolean;

  /**
   * Controlled open state.
   */
  open?: boolean;

  /**
   * Called when the dialog opens or closes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Confirm action visual style.
   */
  variant?: 'default' | 'destructive';
};

/**
 * A confirmation dialog for destructive or important actions. Generated code should only
 * provide a real `onConfirm` handler when the surrounding page already has the action
 * available.
 *
 * @example
 * const voidInvoice = Sdk.hooks.useWorkflowAction({ workflowId: VOID_INVOICE_WORKFLOW_ID });
 *
 * <Sdk.Components.ConfirmDialog
 *   title="Void invoice?"
 *   description={voidInvoice.errorMessage ?? 'This cannot be undone.'}
 *   confirmLabel="Void invoice"
 *   onConfirm={async () => (await voidInvoice.run({ invoice: invoice.id })) !== null}
 *   trigger={<Sdk.Components.Button>Void invoice…</Sdk.Components.Button>}
 * />
 *
 * @example
 * <Sdk.Components.ConfirmDialog
 *   title="Archive record?"
 *   description="This can be restored later."
 *   confirmLabel="Archive"
 *   onConfirm={() => {}}
 *   trigger={<Sdk.Components.Button>Archive…</Sdk.Components.Button>}
 * />
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Confirm',
  onConfirm,
  trigger,
  processing,
  open,
  onOpenChange,
  variant
}: ConfirmDialogProps) {
  return (
    <UiConfirmDialog
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      onConfirm={onConfirm}
      processing={processing}
      isOpen={open}
      onOpenChange={onOpenChange}
      variant={variant}
    >
      {trigger}
    </UiConfirmDialog>
  );
}
